// Pure helpers for the availability widget, split out from
// `useLocationAvailability.tsx` so the day-windowing, time-label parsing and
// colour scale can be unit-tested without pulling in the React hook's
// store/router dependencies.

import moment from 'moment'

export type TimeSlot = { time?: string; availabilities?: string | number }
export type Slot = { date: string; timeSlots?: TimeSlot[] }

/** One rendered day: its open-slot total plus the raw time blocks behind it. */
export type AvailabilityDay = { date: string; count: number; timeSlots: TimeSlot[] }

/** How many months past the current one the month stepper will walk forward. */
export const MAX_MONTH_OFFSET = 3

// The four fixed appointment-type probes. Each is a separate call to the
// `/agent/availabilities` endpoint with a type query param (hygiene has none).
export const APPOINTMENT_TYPES = [`hygiene`, `limitedExam`, `emergency`, `consult`] as const
export type AppointmentTypeKey = (typeof APPOINTMENT_TYPES)[number]

export const APPOINTMENT_LABEL: Record<AppointmentTypeKey, string> = {
  hygiene: `Hygiene`,
  limitedExam: `Limited Exam`,
  emergency: `Emergency`,
  consult: `Consultation`,
}

export const TYPE_QS: Record<AppointmentTypeKey, string | null> = {
  hygiene: null,
  emergency: `isEmergency=true`,
  consult: `isConsult=true`,
  limitedExam: `isLimitedExam=true`,
}

export const ISO_FORMAT = `YYYY-MM-DD`

/** Days in one window: today and the 30 after it. */
export const WINDOW_DAYS = 31

/** An inclusive run of days to render and fetch. */
export type AvailabilityWindow = {
  startISO: string
  endISO: string
  /** Stepper caption, e.g. "Oct 28 to Nov 27". */
  label: string
  /** 0 starts today; the stepper only ever walks forward. */
  offset: number
}

/** The `offset`-th run of 31 days from today, so the first day shown is always today or later. */
export function availabilityWindow(offset: number, today: Date = new Date()): AvailabilityWindow {
  const start = moment(today)
    .startOf(`day`)
    .add(offset * WINDOW_DAYS, `days`)
  const end = start.clone().add(WINDOW_DAYS - 1, `days`)
  return {
    startISO: start.format(ISO_FORMAT),
    endISO: end.format(ISO_FORMAT),
    label: `${start.format(`MMM D`)} to ${end.format(`MMM D`)}`,
    offset,
  }
}

/**
 * Every date in the window, each with the summed open-slot count for that day
 * and the time blocks that make it up. Days the API said nothing about come
 * back as zero — it omits days with no openings entirely.
 */
export function buildWindowDays(slots: Slot[], window: AvailabilityWindow): AvailabilityDay[] {
  const bySlotDate = new Map<string, { count: number; timeSlots: TimeSlot[] }>()
  for (const slot of slots || []) {
    // `availabilities` is a stringified count per time block (e.g. "3"); sum them
    // for the day's total. Normalize the date key to YYYY-MM-DD so it matches
    // whether the API sends a date-only string or an ISO datetime.
    const timeSlots = slot.timeSlots || []
    const count = timeSlots.reduce((sum, ts) => sum + (parseInt(String(ts.availabilities), 10) || 0), 0)
    const dateKey = String(slot.date ?? ``).slice(0, 10)
    if (dateKey) bySlotDate.set(dateKey, { count, timeSlots })
  }

  const days: AvailabilityDay[] = []
  const cursor = moment(window.startISO, ISO_FORMAT)
  const end = moment(window.endISO, ISO_FORMAT)
  while (cursor.isSameOrBefore(end, `day`)) {
    const iso = cursor.format(ISO_FORMAT)
    const match = bySlotDate.get(iso)
    days.push({ date: iso, count: match?.count ?? 0, timeSlots: match?.timeSlots ?? [] })
    cursor.add(1, `day`)
  }
  return days
}

/**
 * The card's headline. What can honestly be said depends on which month is up:
 * a countdown is only meaningful for the month containing today, so a future
 * month names the date of its first opening instead. "Nothing" is always scoped
 * to the window we actually asked about, never left as a bare dash that reads
 * like a failed fetch.
 */
export function headline(
  days: AvailabilityDay[],
  window: AvailabilityWindow,
  today: Date = new Date(),
): { value: string; caption: string } {
  // Only days from today onward count. The fetch already clamps its start date,
  // but a month's data is cached by range — a dashboard left open overnight
  // still holds days that have since gone past, and one of those reading as
  // "today" would be a lie.
  const todayISO = moment(today).format(ISO_FORMAT)
  const firstOpen = days.find((day) => day.count > 0 && day.date >= todayISO)
  const isCurrentWindow = window.offset === 0

  if (!firstOpen) {
    return { value: `None`, caption: isCurrentWindow ? `in the next ${WINDOW_DAYS} days` : `open ${window.label}` }
  }
  if (!isCurrentWindow) {
    return { value: moment(firstOpen.date, ISO_FORMAT).format(`MMM D`), caption: `is the first opening` }
  }

  const daysAway = moment(firstOpen.date, ISO_FORMAT).diff(moment(today).startOf(`day`), `days`)
  if (daysAway <= 0) return { value: `Today`, caption: `has availability` }
  return { value: `${daysAway}d`, caption: `until next open` }
}

/**
 * Which month the left/right arrow keys should move to, or null when the key
 * should be left alone — a bare arrow only steps the month when nothing else
 * has a claim on it. Modifiers are someone else's shortcut (⌘← is browser
 * back), a text field needs its arrows for the caret, and a press against
 * either bound is a no-op rather than pointless state churn.
 */
export function offsetForArrowKey(
  key: string,
  current: number,
  { hasModifier, isTyping }: { hasModifier: boolean; isTyping: boolean },
): number | null {
  if (hasModifier || isTyping) return null
  const step = key === `ArrowLeft` ? -1 : key === `ArrowRight` ? 1 : 0
  if (step === 0) return null
  const next = current + step
  if (next < 0 || next > MAX_MONTH_OFFSET) return null
  return next
}

/**
 * Strips the trailing date the API appends to every time block, turning
 * "9:00 AM - 10:00 AM - Wednesday, September 16, 2026" back into the time range
 * on its own. Every weekday name ends in "day", which is what anchors the cut.
 */
export function extractTimeLabel(time?: string): string {
  return String(time ?? ``).replace(/\s-\s[A-Za-z]+day,\s.*$/, ``)
}

export type HeatTier = {
  /** Highest open-slot count that still falls in this tier. */
  max: number
  /** Background + border for a heat cell. */
  className: string
}

/**
 * The open-slots-per-day colour scale: darker means more openings. Deliberately
 * the inverse of Atlas's scale, which reads dark as scarce — this is a client's
 * own dashboard, so more availability should look like more, not like an alarm.
 */
export const HEAT_TIERS: readonly HeatTier[] = [
  { max: 0, className: `bg-surface-muted border-default` },
  { max: 2, className: `bg-green-200 border-green-200` },
  { max: 5, className: `bg-green-300 border-green-300` },
  { max: 9, className: `bg-green-500 border-green-500` },
  { max: Infinity, className: `bg-green-600 border-green-600` },
]

/** A day before today: never fetched, so it draws blank rather than as "zero open". */
export const PAST_CELL_CLASS = `bg-surface border-subtle`

export function tierForCount(count: number): HeatTier {
  return HEAT_TIERS.find((tier) => count <= tier.max) ?? HEAT_TIERS[HEAT_TIERS.length - 1]
}
